import {
  createSession,
  getEmptySessions,
  initializeDbWebSocket,
  initializeOlWebSocket,
  initializeReplayWebSocket,
  isSessionDelivering,
  retireSession,
  Session,
  Sessions,
} from './createSession';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { memoryDiagnostics } from '@ais-ui/utils';
import * as Sentry from '@sentry/react';
import { primeMapView } from './primeMapView';
type SessionType = 'dbSession' | 'replaySession' | 'olSession';

/** Backoff ceiling for sessions that don't set their own. */
const DEFAULT_MAX_BACKOFF_DELAY_MS = 180_000;
const INITIAL_BACKOFF_DELAY_MS = 1_000;

type ServerConfigs = {
  [key in SessionType]: {
    initialize: (hostname: string, protocol: string) => Promise<any>;
    name: SessionType;
    maxBackoffDelay?: number;
  };
};

// Configuration for each server type. All three retry indefinitely with
// capped exponential backoff — `useReconnect`'s only job is to keep the
// transport retrying. Deciding "we've waited long enough" for the
// app-critical olSession is owned entirely by `useGracefulLogout`
// (single source of truth for that decision), not by an attempt count here.
//
// Module-level because it closes over nothing but the imports above: it is a
// constant, and expressing it as one is clearer than a `useMemo` with an
// empty dependency array.
const SERVER_CONFIGS: ServerConfigs = {
  dbSession: {
    initialize: initializeDbWebSocket,
    name: 'dbSession',
    maxBackoffDelay: DEFAULT_MAX_BACKOFF_DELAY_MS,
  },
  replaySession: {
    initialize: initializeReplayWebSocket,
    name: 'replaySession',
    maxBackoffDelay: DEFAULT_MAX_BACKOFF_DELAY_MS,
  },
  olSession: {
    initialize: initializeOlWebSocket,
    name: 'olSession',
    maxBackoffDelay: 10_000,
  },
};

// Helper function to determine if we should log this reconnection attempt
const shouldLogReconnectionAttempt = (retryCount: number): boolean => {
  // Log first 5 attempts (0-4)
  if (retryCount < 5) {
    return true;
  }
  // Log every 10th attempt from 5 to 99
  if (retryCount < 100 && retryCount % 10 === 0) {
    return true;
  }
  // Log every 100th attempt from 100 onwards
  if (retryCount >= 100 && retryCount % 100 === 0) {
    return true;
  }
  return false;
};

// Retire the transport a reconnect has just replaced. Both guards are the
// reason this is a named function rather than an inline condition: a reconnect
// triggered while the previous session is already gone (the ordinary
// `connectionClosed` path clears it first) has nothing to retire, and the
// replacement must never be torn down by the very call that installed it.
const retireSupersededSession = (
  previous: Session | undefined,
  replacement: Session,
): void => {
  if (!previous || previous === replacement) {
    return;
  }
  retireSession(previous);
};

/**
 * A session handed to `setSessions`, awaiting confirmation that React
 * committed it. `sessionId` is the value the updater will check this install
 * against, and is what distinguishes "declined" from "not committed yet".
 */
type PendingInstall = {
  session: Session;
  sessionId: string;
};

type PendingInstalls = Partial<Record<SessionType, PendingInstall>>;

// `setSessions`' updater can decline an install: it re-checks the sessionId
// against the value React is committing, which sees a logout that the ref
// (only refreshed on commit) does not. A declined session is a live socket
// with its handlers attached that no state points at - precisely the orphan
// this hook exists to prevent - so it has to be retired rather than dropped.
//
// Absence from `committed` is NOT enough to conclude that: each slot
// reconnects on its own schedule, so a commit carrying one slot can land
// while another slot's install is still queued. Retiring on absence would
// close a socket React then installs, leaving a dead transport in state.
// Mirroring the updater's own test - a sessionId mismatch - is what makes
// the two decisions agree. Returns the entries still awaiting a commit.
const reconcilePendingInstalls = (
  pending: PendingInstalls,
  committed: Sessions | undefined,
): PendingInstalls => {
  const stillPending: PendingInstalls = {};

  for (const [slot, entry] of Object.entries(pending)) {
    if (!entry) continue;
    const sessionType = slot as SessionType;

    if (committed?.[sessionType] === entry.session) continue;

    if (committed?.sessionId !== entry.sessionId) {
      retireSession(entry.session);
      continue;
    }

    stillPending[sessionType] = entry;
  }

  return stillPending;
};

/** Capped exponential backoff: 1s, 2s, 4s, 8s... up to the session's ceiling. */
const calculateBackoffDelay = (
  retryCount: number,
  maxBackoffDelay?: number,
): number =>
  Math.min(
    INITIAL_BACKOFF_DELAY_MS * 2 ** retryCount,
    maxBackoffDelay || DEFAULT_MAX_BACKOFF_DELAY_MS,
  );

/** The backoff retry waiting to run, at most one per session type. */
type ScheduledRetries = Partial<
  Record<SessionType, ReturnType<typeof setTimeout>>
>;

/** Identity of the attempt holding a session type's in-flight guard. */
type InFlightAttempt = { sessionId: string | undefined };

const cancelScheduledRetry = (
  retries: ScheduledRetries,
  sessionType: SessionType,
): void => {
  clearTimeout(retries[sessionType]);
  delete retries[sessionType];
};

const cancelAllScheduledRetries = (retries: ScheduledRetries): void => {
  for (const sessionType of Object.keys(retries) as SessionType[]) {
    cancelScheduledRetry(retries, sessionType);
  }
};

// Individual reconnection attempts are self-healing and must NOT be reported
// to Sentry (issue 3F floods the project), so throttled console output is the
// only record of one. There is no give-up here either - reconnects continue
// indefinitely; for olSession it is `useGracefulLogout` (owned by the caller)
// that decides when enough time has passed to log the user out.
const logReconnectionRetry = (
  sessionType: SessionType,
  retryCount: number,
  delayMs: number,
  error?: unknown,
): void => {
  if (!shouldLogReconnectionAttempt(retryCount)) {
    return;
  }

  const attempt = retryCount + 1;
  const seconds = delayMs / 1000;

  if (error !== undefined) {
    console.warn(
      `Error during reconnection attempt ${attempt} to ${sessionType}, retrying in ${seconds} seconds`,
      error,
    );
    return;
  }

  console.warn(
    `Reconnection attempt ${attempt} failed to connect to ${sessionType}, retrying in ${seconds} seconds`,
  );
};

const reportBlockedReconnect = (sessionType: SessionType): void => {
  console.warn(`Cannot reconnect ${sessionType}: No sessionId available`);
  Sentry.captureMessage(
    `Reconnection attempt blocked for ${sessionType}: No sessionId`,
    'warning',
  );
};

const reportSessionCreationFailure = (
  sessionType: SessionType,
  retryCount: number,
): void => {
  Sentry.captureMessage(
    `Failed to create ${sessionType} session on attempt ${retryCount + 1}`,
    'error',
  );
  console.warn(
    `Reconnection attempt ${
      retryCount + 1
    } failed to create ${sessionType} session`,
  );
};

export const useReconnect = (
  sessions: Sessions,
  setSessions: Dispatch<SetStateAction<Sessions>>,
  /** Passed straight through to `primeMapView` (see primeMapView.ts) for a
   * fresh `olSession` reconnect. Optional/undefined is a no-op. */
  initializeMapView?: (session: Session) => Promise<void>,
) => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Always read the freshest sessions from a ref so retries scheduled via
  // setTimeout don't bake a stale sessionId (captured in an old closure) into
  // the session object. This is what previously allowed an olSession to be
  // created with an empty/stale sessionId during reconnection.
  const sessionsRef = useRef(sessions);
  const pendingInstallsRef = useRef<PendingInstalls>({});
  // One reconnect attempt may be in flight per session type and sessionId.
  // Kept in a ref so it survives `reconnectSession` being recreated when a
  // dependency (e.g. `initializeMapView`) changes mid-flight. See the guard in
  // `reconnectSession` for why this exists and why it can never stick.
  const reconnectsInFlightRef = useRef<
    Partial<Record<SessionType, InFlightAttempt>>
  >({});
  // A scheduled retry belongs to the chain - and the session - that scheduled
  // it. Left to fire regardless it would outlive both: tear down a transport
  // a newer attempt or a fresh login just installed, run after logout, or keep
  // reconnecting for an unmounted hook with nothing left to reconcile against.
  const scheduledRetriesRef = useRef<ScheduledRetries>({});
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Every sessionId change (login, restore, logout) brings a complete set of
  // sessions of its own, so the old session's chains have nothing left to do.
  // Also runs on unmount.
  const sessionId = sessions?.sessionId;
  useEffect(() => {
    const scheduledRetries = scheduledRetriesRef.current;
    return () => cancelAllScheduledRetries(scheduledRetries);
  }, [sessionId]);

  useEffect(() => {
    // Reconcile before adopting the commit: everything this hook handed to
    // `setSessions` has now either landed in `sessions`, been declined, or is
    // still queued behind this commit.
    pendingInstallsRef.current = reconcilePendingInstalls(
      pendingInstallsRef.current,
      sessions,
    );

    sessionsRef.current = sessions;
  }, [sessions]);

  const reconnectSession = useCallback(
    async (sessionType: SessionType, retryCount = 0) => {
      // Serialize attempts per session type: an attempt already in flight for
      // this type owns the slot until it settles, so a second trigger (a
      // `connectionClosed`, a grace-period validity retry, or this hook's own
      // backoff) is skipped rather than started alongside it. The predecessor
      // is only retired once the replacement is established - after priming,
      // which can wait up to PRIME_MAP_VIEW_TIMEOUT_MS - so overlapping
      // attempts would leave two live sockets re-dispatching global events
      // (CLASSIC-10369).
      //
      // `finally` is mandatory: a stuck flag would stop reconnects permanently
      // and the app could never recover, so it must be cleared on every exit
      // path - success, failure, early return, or throw.
      //
      // The slot is only owned per sessionId. An attempt started for a
      // previous session will neither install nor retry (see
      // `isSessionStillCurrent`), so it must not swallow the new session's
      // reconnect - nor, when it settles, release the guard a newer attempt
      // now holds.
      //
      // Read the current sessionId at execution time (see sessionsRef above),
      // not the value captured when this callback was created.
      const currentSessionId = sessionsRef.current?.sessionId;
      const inFlight = reconnectsInFlightRef.current[sessionType];
      if (inFlight && inFlight.sessionId === currentSessionId) {
        return;
      }
      const attempt: InFlightAttempt = { sessionId: currentSessionId };
      reconnectsInFlightRef.current[sessionType] = attempt;
      // One chain per type: this attempt takes over from any retry still
      // waiting, which would otherwise replace whatever this one installs.
      cancelScheduledRetry(scheduledRetriesRef.current, sessionType);

      try {
        const config = SERVER_CONFIGS[sessionType];
        memoryDiagnostics.trackReconnect(sessionType);

        if (!currentSessionId) {
          reportBlockedReconnect(sessionType);
          return;
        }

        // A reconnect can outlive the session it was started for: `logout()`
        // and a session restore both replace `sessions` while connect/prime
        // are still awaiting, and the hook itself can unmount. From then on
        // this chain must neither install nor retry.
        const isSessionStillCurrent = () =>
          isMountedRef.current &&
          sessionsRef.current?.sessionId === currentSessionId;

        // Both failure paths below retry identically; the only difference is
        // whether there's an error to log alongside it.
        const retryWithBackoff = (error?: unknown) => {
          if (!isSessionStillCurrent()) {
            return;
          }
          const delay = calculateBackoffDelay(
            retryCount,
            config.maxBackoffDelay,
          );
          logReconnectionRetry(sessionType, retryCount, delay, error);
          const scheduledRetries = scheduledRetriesRef.current;
          cancelScheduledRetry(scheduledRetries, sessionType);
          scheduledRetries[sessionType] = setTimeout(() => {
            delete scheduledRetries[sessionType];
            reconnectSession(sessionType, retryCount + 1);
          }, delay);
        };

        try {
          const { webSocket, serverAndClient } = await config.initialize(
            hostname,
            protocol,
          );

          if (!webSocket || !serverAndClient) {
            retryWithBackoff();
            return;
          }

          // Create the session object using the freshest sessionId from state
          // (captured above), so we never bake a stale/empty value into it.
          const session = createSession(
            serverAndClient,
            webSocket,
            currentSessionId,
            config.name,
          );

          if (!session) {
            reportSessionCreationFailure(sessionType, retryCount);
            return;
          }

          // Checked before priming as well as after: priming sends a request
          // stamped with `currentSessionId`, and for a login that ended while
          // this socket was connecting the backend's refusal would only be a
          // session signal about a session nobody holds any more.
          if (!isSessionStillCurrent()) {
            retireSession(session);
            return;
          }

          // Prime the backend mapview for a fresh olSession (best-effort,
          // never throws - see primeMapView.ts). dbSession/replaySession have
          // no mapview concept and never call it.
          let isPrimed = true;
          if (sessionType === 'olSession') {
            isPrimed = await primeMapView(
              initializeMapView,
              session,
              'reconnect',
            );
          }

          // `getEmptySessions()` is truthy, so the `!prevSessions` guard below
          // does not catch a logout - without this check a late reconnect
          // reinstalls a live socket into a logged-out app (or an unmounted
          // hook), and nothing would ever retire it.
          if (!isSessionStillCurrent()) {
            retireSession(session);
            return;
          }

          // A replacement that could not be primed has no mapview and cannot
          // serve overlays, so it must not displace a socket that is still
          // delivering. A silent predecessor gains nothing from being kept.
          if (
            !isPrimed &&
            isSessionDelivering(sessionsRef.current?.[config.name])
          ) {
            retireSession(session);
            return;
          }

          // Claim the slot synchronously, then install. `sessionsRef` is the
          // only view of "current" that doesn't wait for a React commit, so
          // reading the predecessor and writing the replacement happen in one
          // uninterrupted step. The in-flight guard above now stops same-type
          // attempts from overlapping, but this stays as defence in depth: the
          // read and the write must never be split by an `await`.
          const supersededSession = sessionsRef.current?.[config.name];
          if (sessionsRef.current) {
            sessionsRef.current = {
              ...sessionsRef.current,
              [config.name]: session,
            };
          }
          // Part of the same claim: the ledger is what lets the next commit
          // tell "installed" from "declined". A concurrent reconnect
          // overwriting this entry is correct - it also took over the ref
          // slot, and it retires this session as its predecessor.
          pendingInstallsRef.current[config.name] = {
            session,
            sessionId: currentSessionId,
          };

          setSessions((prevSessions) => {
            if (!prevSessions) return getEmptySessions();
            // Re-applied against the value React is actually committing:
            // `sessionsRef` only catches up on commit, so a logout landing in
            // that window is still visible here. Returning `prevSessions`
            // unchanged keeps this updater pure; the session it just declined
            // is retired by the reconciliation effect, which compares this
            // pending install against what React ends up committing.
            if (prevSessions.sessionId !== currentSessionId)
              return prevSessions;
            return {
              ...prevSessions,
              [config.name]: session,
            };
          });

          // Only on the success path: a failed attempt leaves the old socket
          // exactly as it was, so nothing is torn down until there is something
          // to take its place. Both the claim above and `setSessions` are
          // last-write-wins in the same order, so the session left installed is
          // never one that was retired here.
          retireSupersededSession(supersededSession, session);

          // Transport reconnect is intentionally silent for every session type.
          // useReconnect only reopens the socket, primes mapview for a fresh
          // olSession, and updates session state. useGracefulLogout owns the one
          // user-facing success toast after olSession validity is confirmed;
          // dbSession/replaySession stay silent background transports.
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              serverType: sessionType,
              retryCount,
            },
            extra: {
              sessionId: currentSessionId,
            },
          });

          retryWithBackoff(error);
        }
      } finally {
        if (reconnectsInFlightRef.current[sessionType] === attempt) {
          delete reconnectsInFlightRef.current[sessionType];
        }
      }
    },
    [hostname, protocol, setSessions, initializeMapView],
  );

  const reconnect = useCallback(
    (sessionType: string) => {
      if (
        sessionType === 'dbSession' ||
        sessionType === 'replaySession' ||
        sessionType === 'olSession'
      ) {
        return reconnectSession(sessionType);
      } else {
        console.warn('Unknown session type, not reconnecting');
        Sentry.captureMessage(
          `Attempted to reconnect to unknown session type: ${sessionType}`,
          'error',
        );
      }
    },
    [reconnectSession],
  );

  return reconnect;
};
